'use client';

import { VariantPrompt } from '@/lib/types';
import api from '@/lib/api';

interface VariantPreviewCardProps {
  prompt: VariantPrompt;
  isSelected: boolean;
  onToggleSelect: (promptId: number) => void;
  onEnlarge: () => void;
}

export default function VariantPreviewCard({ prompt, isSelected, onToggleSelect, onEnlarge }: VariantPreviewCardProps) {
  const handleCardClick = () => {
    // Click anywhere on the card to enlarge
    onEnlarge();
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (prompt.preview_status === 'completed') {
      onToggleSelect(prompt.id);
    }
  };

  return (
    <div
      className={`relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
        isSelected
          ? 'border-[var(--accent)] shadow-lg shadow-[var(--accent)]/20'
          : 'border-transparent hover:border-[var(--border-subtle)]'
      }`}
      onClick={handleCardClick}
    >
      {/* Selection checkbox overlay */}
      {prompt.preview_status === 'completed' && (
        <div className="absolute top-2 left-2 z-10" onClick={handleCheckboxClick}>
          <div
            className={`w-6 h-6 rounded-md flex items-center justify-center transition-all cursor-pointer ${
              isSelected
                ? 'bg-[var(--accent)] text-black hover:bg-[var(--accent-hover)]'
                : 'bg-black/60 text-white/60 hover:bg-black/80'
            }`}
          >
            {isSelected ? (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
              </svg>
            ) : (
              <span className="text-xs font-medium">{prompt.sort_order + 1}</span>
            )}
          </div>
        </div>
      )}

      {/* Image preview */}
      <div className="aspect-square relative overflow-hidden bg-[var(--bg-elevated)]">
        {prompt.preview_status === 'completed' && prompt.preview_path && (
          <img
            src={api.getThumbnailUrl(prompt.preview_path)}
            alt={prompt.prompt}
            className={`w-full h-full object-cover transition-all duration-300 ${
              isSelected ? 'scale-105' : 'group-hover:scale-105'
            }`}
            loading="lazy"
          />
        )}

        {prompt.preview_status === 'processing' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <svg className="w-8 h-8 mx-auto text-[var(--accent)] animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <span className="block mt-2 text-xs text-[var(--text-muted)]">Generating...</span>
            </div>
          </div>
        )}

        {prompt.preview_status === 'pending' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <span className="text-2xl font-light text-[var(--text-muted)]">#{prompt.sort_order + 1}</span>
              <span className="block text-xs text-[var(--text-muted)] mt-1">waiting</span>
            </div>
          </div>
        )}

        {prompt.preview_status === 'failed' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center px-4">
              <svg className="w-8 h-8 mx-auto text-[var(--error)]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              <span className="block mt-2 text-xs text-[var(--error)]">Failed</span>
            </div>
          </div>
        )}
      </div>

      {/* Prompt text */}
      <div className="p-3 bg-[var(--bg-surface)]">
        <p
          className="text-xs text-[var(--text-secondary)] line-clamp-2 leading-snug"
          title={prompt.prompt}
        >
          {prompt.prompt}
        </p>
      </div>
    </div>
  );
}
